import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import ErrorBanner from '../../components/shared/ErrorBanner'
import InlineLoading from '../../components/shared/InlineLoading'
import {
    holidayIdError,
    emptyHolidayForm,
    holidayPayload,
    validateHoliday,
} from '../../helpers/holidays'
import HolidayService from '../../services/holidayService'
import { errorMessage } from '../../services/axios/errorMessage'
import { TEXT, cardSx, pageSx } from '../../theme/tokens'
import HolidayForm from './HolidayForm'
import HolidayHeader from './HolidayHeader'

/**
 * Create a holiday, step 1 of 2.
 *
 * As in the applause app, a new holiday is saved together with its
 * pipeline: this page checks the details, then hands them to the pipeline
 * wizard (event type = holiday ID, Inline audience, one-time, absolute
 * time). The holiday is stored only once that pipeline is created, so a
 * holiday never exists without the pipeline that sends its comms.
 */
const CreateHoliday = () => {
    const navigate = useNavigate()
    const [form, setForm] = useState(emptyHolidayForm)
    const [takenIds, setTakenIds] = useState(null)
    const [error, setError] = useState('')

    useEffect(() => {
        HolidayService.takenIds()
            .then(setTakenIds)
            .catch((err) =>
                setError(
                    `Existing holidays could not be checked: ${errorMessage(
                        err?.response
                    )}`
                )
            )
    }, [])

    const onContinue = () => {
        const problem = validateHoliday(form, {
            creating: true,
            takenIds: takenIds ?? [],
        })
        if (problem) {
            setError(problem)
            return
        }
        navigate('/pipelines/new', {
            state: { holiday: holidayPayload(form) },
        })
    }

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 960, mx: 'auto', p: { xs: 3, md: 4 } }}>
                <HolidayHeader
                    title="New holiday"
                    subtitle="Step 1 of 2: the holiday. Step 2 creates the pipeline that sends its comms."
                />
                <Paper elevation={0} sx={{ ...cardSx, p: 3 }}>
                    <HolidayForm
                        form={form}
                        creating
                        onChange={(patch) => {
                            setForm((prev) => ({ ...prev, ...patch }))
                            setError('')
                        }}
                    />
                    <Typography
                        sx={{
                            typography: 'caption',
                            color: TEXT.muted,
                            mt: 2.5,
                        }}
                    >
                        Dates and times are IST. Comms go out at the comms time,
                        that many days before the holiday.
                    </Typography>
                    {error && (
                        <Box sx={{ mt: 2 }}>
                            <ErrorBanner message={error} />
                        </Box>
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            mt: 3,
                        }}
                    >
                        {/* Continue waits for the ids a new holiday must not
                            reuse; say so instead of a silently disabled button. */}
                        {takenIds === null && !error && (
                            <InlineLoading
                                label="Checking existing holidays…"
                                py={0}
                            />
                        )}
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/holidays')}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={onContinue}
                            disabled={
                                takenIds === null ||
                                !!holidayIdError(form.holiday_id)
                            }
                        >
                            Continue to pipeline
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}

export default CreateHoliday
